import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../hooks/useAuth.js';

/**
 * Honour a `?workspace=<id>` hint on an incoming deep link.
 *
 * Another product (Traceira Deal Flow) links straight to /invoices for the
 * workspace it raises invoices into. The active workspace lives in the session,
 * not the URL, so without this a user with access to several — a super admin
 * especially — lands in whichever one their last session held, which is rarely
 * the one the link meant.
 *
 * Only switches to a workspace the user already has; an unknown id is ignored
 * rather than treated as an authorisation hint. The parameter is stripped
 * either way so a refresh cannot re-trigger the switch.
 */
export function WorkspaceDeepLink() {
  const { workspaceId, workspaces, switchWorkspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const handling = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requested = params.get('workspace');
    if (!requested || handling.current) return;

    const stripParam = () => {
      params.delete('workspace');
      const search = params.toString();
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    };

    // Not signed in yet: leave the parameter for the next render, after the
    // session resolves, rather than discarding the caller's intent.
    if (!workspaces || workspaces.length === 0) return;

    const target = workspaces.find(
      (w) => w.workspaceId === requested || w.companyId === requested,
    );

    if (!target || target.workspaceId === workspaceId) {
      stripParam();
      return;
    }

    handling.current = true;
    switchWorkspace(target.workspaceId)
      .catch(() => {
        // A failed switch leaves the user where they were; the surfaced error
        // belongs to the switcher, not to a background deep-link handler.
      })
      .finally(() => {
        handling.current = false;
        stripParam();
      });
  }, [location.search, location.pathname, workspaceId, workspaces, switchWorkspace, navigate]);

  return null;
}
